import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getOrganizationDetails, getOrganizations } from 'Api/UcrmTprm/UcrmTprmApi';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { TPRM, UCRM } from 'Config/Paths';
import { BreadcrumbSegment, getManageExceptionOriginBreadcrumbSegments, parseManageExceptionOrigin } from 'Helpers/URLBuilder/URLBuilder';
import { useControlMappingItems } from 'Hooks/ControlMapping';
import { OrganizationResponseWithAssessmentSubjectsBase } from 'Models/AssessmentSubject';
import { ExceptionResponse } from 'Models/Exceptions';
import { ActiveState, OrganizationResponseBase } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import { ManageExceptionForm, ManageExceptionFormProps } from '../Components/ManageExceptionForm/ManageExceptionForm';

export interface ManageExceptionProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    exceptionsApi: ExceptionsApi;
    controlsApi: ControlsApi;
    documentApi: DocumentApi;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

/**
 * Ultimately renders a page that can be used to create, update, or delete an exception in the Operational Controls module or in the TPRM module.
 * This component is responsible for examining the URL to determine the type of exception being managed. It determines which single child needs to be rendered; the child is responsible for fetching data specific to the user's desired action and for passing that data into `ManageExceptionForm`.
 *
 * This structure/flow is used for two main reasons:
 * 1. To avoid fetching data that is not needed for the user's desired action. (For example, if the user is managing a TPRM exception, we do not want to use `useControlMappingItems`.)
 * 2. To improve readability. Without these intermediary components, it becomes very difficult to understand which data is supposed to be fetched and when it will be available.
 */
export const ManageException = (props: ManageExceptionProps) => {
    const { exceptionId } = useParams();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const impactedEntityId = query.get('impactedEntityId') ?? undefined;
    const exceptionType = location.pathname.startsWith(`/${TPRM}`) ? ProductModule.TPRM : location.pathname.startsWith(`/${UCRM}`) ? ProductModule.UCRM : ProductModule.CONTROLS;
    const isClosingException = query.get('closing') === String(true);

    const origin = parseManageExceptionOrigin(query);
    const originBreadcrumbSegments = getManageExceptionOriginBreadcrumbSegments(origin);

    switch (exceptionType) {
        case ProductModule.CONTROLS:
            if (exceptionId) {
                return <EditControlException {...props} exceptionId={exceptionId} isClosingException={isClosingException} originBreadcrumbSegments={originBreadcrumbSegments} />;
            } else {
                return <CreateControlException {...props} preselectedControlId={impactedEntityId} originBreadcrumbSegments={originBreadcrumbSegments} />;
            }
        case ProductModule.TPRM:
        case ProductModule.UCRM:
            if (exceptionId) {
                return <EditOrganizationException {...props} exceptionId={exceptionId} isClosingException={isClosingException} originBreadcrumbSegments={originBreadcrumbSegments} />;
            } else {
                return <CreateOrganizationException {...props} module={exceptionType} preselectedOrganizationId={impactedEntityId} originBreadcrumbSegments={originBreadcrumbSegments} />;
            }
    }
};

/**
 * Renders `ManageExceptionForm` for the use case of creating an Operational Controls exception, after fetching all controls to which the exception can be mapped.
 */
const CreateControlException = (props: ManageExceptionProps & { preselectedControlId?: string; originBreadcrumbSegments: BreadcrumbSegment[] }) => {
    const [controlMappingItems, controlMappingItemsError] = useControlMappingItems(props.controlsApi);

    if (controlMappingItemsError) {
        return <Text>{controlMappingItemsError.message}</Text>;
    } else if (controlMappingItems === undefined) {
        return <Placeholder />;
    } else {
        const manageExceptionFormProps: ManageExceptionFormProps = {
            type: 'creatingControlException',
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            exceptionsApi: props.exceptionsApi,
            documentApi: props.documentApi,
            originBreadcrumbSegments: props.originBreadcrumbSegments,
            controls: controlMappingItems,
            preselectedControlId: props.preselectedControlId,
        };
        return <ManageExceptionForm {...manageExceptionFormProps} />;
    }
};

/**
 * Renders `ManageExceptionForm` for the use case of managing an Operational Controls exception, after fetching details for the exception and all controls to which the exception can be mapped.
 */
const EditControlException = (props: ManageExceptionProps & { exceptionId: string; isClosingException: boolean; originBreadcrumbSegments: BreadcrumbSegment[] }) => {
    const [controlMappingItems, controlMappingItemsError] = useControlMappingItems(props.controlsApi);
    const [exception, setException] = useState<ExceptionResponse>();
    const [requestError, setRequestError] = useState<Error>();

    useEffect(() => {
        const getException = async (): Promise<void> => {
            try {
                const response = await props.exceptionsApi.getException(props.exceptionId);
                setException(response.data as ExceptionResponse);
            } catch (error) {
                setRequestError(error);
            }
        };

        getException();
    }, [props.exceptionsApi, props.exceptionId]);

    if (controlMappingItemsError) {
        return <Text>{controlMappingItemsError.message}</Text>;
    } else if (requestError) {
        return <Text>{requestError.message}</Text>;
    } else if (controlMappingItems === undefined || exception === undefined) {
        return <Placeholder />;
    } else {
        const manageExceptionFormProps: ManageExceptionFormProps = {
            type: 'editingControlException',
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            exceptionsApi: props.exceptionsApi,
            documentApi: props.documentApi,
            originBreadcrumbSegments: props.originBreadcrumbSegments,
            controls: controlMappingItems,
            exception: exception,
            isDraftingForClosure: props.isClosingException,
        };
        return <ManageExceptionForm {...manageExceptionFormProps} />;
    }
};

/**
 * Renders `ManageExceptionForm` for the use case of creating a TPRM or UCRM exception, after fetching all of the module's active organizations (third parties or businesses) to which the exception can be mapped.
 */
const CreateOrganizationException = (props: ManageExceptionProps & { module: DueDiligenceModule; preselectedOrganizationId?: string; originBreadcrumbSegments: BreadcrumbSegment[] }) => {
    const [organizations, setOrganizations] = useState<OrganizationResponseWithAssessmentSubjectsBase[]>();
    const [requestError, setRequestError] = useState<Error>();

    useEffect(() => {
        const fetchOrganizations = async (): Promise<void> => {
            try {
                const response = await getOrganizations(props.moduleApi);
                setOrganizations(response.data.filter((organization) => organization.active_state === ActiveState.ACTIVE));
            } catch (error) {
                setRequestError(error);
            }
        };

        fetchOrganizations();
    }, [props.moduleApi]);

    if (requestError) {
        return <Text>{requestError.message}</Text>;
    } else if (organizations === undefined) {
        return <Placeholder />;
    } else {
        const manageExceptionFormProps: ManageExceptionFormProps = {
            type: 'creatingOrganizationException',
            module: props.module,
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            exceptionsApi: props.exceptionsApi,
            documentApi: props.documentApi,
            originBreadcrumbSegments: props.originBreadcrumbSegments,
            organizations: organizations,
            preselectedOrganizationId: props.preselectedOrganizationId,
        };
        return <ManageExceptionForm {...manageExceptionFormProps} />;
    }
};

/**
 * Renders `ManageExceptionForm` for the use case of managing a TPRM or UCRM exception, after fetching details for the exception and its impacted organization.
 */
const EditOrganizationException = (props: ManageExceptionProps & { exceptionId: string; isClosingException: boolean; originBreadcrumbSegments: BreadcrumbSegment[] }) => {
    const [exception, setException] = useState<ExceptionResponse>();
    const [organization, setOrganization] = useState<OrganizationResponseBase>();
    const [requestError, setRequestError] = useState<Error>();

    useEffect(() => {
        const getException = async (): Promise<void> => {
            try {
                const response = await props.exceptionsApi.getException(props.exceptionId);
                setException(response.data as ExceptionResponse);
            } catch (error) {
                setRequestError(error);
            }
        };

        getException();
    }, [props.exceptionsApi, props.exceptionId]);

    useEffect(() => {
        const fetchOrganization = async (organizationId: string): Promise<void> => {
            try {
                const response = await getOrganizationDetails(props.moduleApi, organizationId);
                setOrganization(response.data);
            } catch (error) {
                setRequestError(error);
            }
        };

        if (exception) {
            fetchOrganization(exception.impacted_vendor!);
        }
    }, [props.moduleApi, exception]);

    if (requestError) {
        return <Text>{requestError.message}</Text>;
    } else if (organization === undefined || exception === undefined) {
        return <Placeholder />;
    } else {
        const manageExceptionFormProps: ManageExceptionFormProps = {
            type: 'editingOrganizationException',
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            exceptionsApi: props.exceptionsApi,
            documentApi: props.documentApi,
            originBreadcrumbSegments: props.originBreadcrumbSegments,
            exception: exception,
            organization: organization,
            isDraftingForClosure: props.isClosingException,
        };
        return <ManageExceptionForm {...manageExceptionFormProps} />;
    }
};
